import { useCallback, useEffect, useRef, useState } from "react";
import "./style/app.css";
import Frame from "./components/frame";
import Profile from "./components/profile";
import CoinBtn from "./components/coinBtn";
import starCoin from "./images/starCoin.png";
import dark1 from "./images/001Dark.png";
import dark2 from "./images/002Dark.png";
import dark3 from "./images/003Dark.png";
import winnerCard1 from "./images/Group 6.png";
import winnerCard2 from "./images/Group 3.png";
import winnerCard3 from "./images/Group 1.png";
import light1 from "./images/001Light.png";
import light2 from "./images/002Light.png";
import light3 from "./images/003Light.png";
import coin2k from "./images/2K.png";
import coin5k from "./images/5K.png";
import coin10k from "./images/10K.png";
import coin20k from "./images/20K.png";
import coin50k from "./images/50K.png";
import audioPath from "./sounds/coins-sound.mp3";
import musicOn from "./images/music-on.png";
import musicOff from "./images/music-off.png";
import historyIcon from "./images/history.png";
import rulesIcon from "./images/rules.png";
import clock from "./images/clock.png";

import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import History from "./components/History";
import Rulse from "./components/Rulse";
import GameBlock from "./components/GameBlock";
import Toast from "./components/Toast";
import RoundResultPanel from "./components/RoundResultPanel";
import { CARD_IMAGES } from "./components/cardImages";
import { buildMyResult, buildTopWinners } from "./components/resultModel";

import { api, makeNonce } from "./api";
import { haptic, requestBalanceRefresh } from "./bridge";
import {
  connectSocket,
  subscribeLobby,
  ensureSubscribed,
  onSocketLifecycle,
  on,
  disconnectSocket,
} from "./socket";

const DARK_FRAMES = [dark1, dark2, dark3];
const LIGHT_FRAMES = [light1, light2, light3];
const WINNER_FRAMES = [winnerCard1, winnerCard2, winnerCard3];
const COIN_IMAGES = [coin2k, coin5k, coin10k, coin20k, coin50k];

// Every server push we treat as proof the engine is alive.
const SERVER_EVENTS = [
  "teenpatti_state",
  "teenpatti_round_snapshot",
  "teenpatti_round_started",
  "teenpatti_bet_placed",
  "teenpatti_round_revealing",
  "teenpatti_round_finished",
  "teenpatti_time_sync",
];
// No push for this long and we stop promising a round is "starting".
const SERVER_QUIET_MS = 10000;
const WATCHDOG_TICK_MS = 3000;
const MIN_RESYNC_GAP_MS = 5000;
// Back off while the server stays idle, so a dead engine is not polled hard by
// every open table.
const MAX_RESYNC_GAP_MS = 30000;

const EMPTY_CARDS = [
  [0, 0, 0],
  [0, 0, 0],
  [0, 0, 0],
];
const EMPTY_RIBBONS = [
  [false, 0, ""],
  [false, 0, ""],
  [false, 0, ""],
];

/**
 * Deterministic 0..1 PRNG so the scattered pot coins keep the same positions
 * across re-renders of the same round instead of jumping on every state change.
 */
function seededUnit(seed) {
  let h = 2166136261;
  const str = String(seed);
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return ((h >>> 0) % 10000) / 10000;
}

/**
 * Visual only: break a frame's pot into coin sprites so the table looks like
 * the original. The source scattered one sprite per individual bet, which
 * required broadcasting every player's stake; the server now publishes only
 * per-frame totals, so we depict the same pot with a capped number of sprites.
 */
function potCoins(roundId, frameId, total, denominations) {
  if (!total || !denominations.length) return [];
  const coins = [];
  let remaining = total;
  const sorted = [...denominations].sort((a, b) => b - a);
  for (const denom of sorted) {
    while (remaining >= denom && coins.length < 12) {
      coins.push(denom);
      remaining -= denom;
    }
    if (coins.length >= 12) break;
  }
  return coins.map((coin, i) => ({
    coin,
    left: 5 + seededUnit(`${roundId}:${frameId}:${i}:l`) * 55,
    top: seededUnit(`${roundId}:${frameId}:${i}:t`) * 45,
  }));
}

/**
 * Fallback payout estimate. Used during the reveal phase, before settlement has
 * reported, and afterwards only if we still don't know our own user id and so
 * cannot read the authoritative figure out of the broadcast `winners[]`. The
 * panel labels it `est.` precisely because it can disagree with what the settle
 * transaction actually credited.
 */
function estimateTeenPattiWin(cfg, winningFrameId, stakes) {
  const staked = Number((stakes || {})[winningFrameId] || 0);
  if (staked <= 0) return 0;
  return Math.floor(staked * Number((cfg && cfg.payoutMultiplier) || 2.9));
}

function App() {
  const [config, setConfig] = useState(null);
  const [round, setRound] = useState(null);
  const [balance, setBalance] = useState(0);
  const [betsByFrame, setBetsByFrame] = useState({});
  const [myStakes, setMyStakes] = useState({ 1: 0, 2: 0, 3: 0 });
  const [leaderboard, setLeaderboard] = useState([]);
  const [remainingMs, setRemainingMs] = useState(0);
  const [selectedCoin, setSelectedCoin] = useState(null);
  const [lastBet, setLastBet] = useState(null);
  const [result, setResult] = useState(null);
  const [soundOn, setSoundOn] = useState(true);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const [fatalError, setFatalError] = useState(null);
  const [serverQuiet, setServerQuiet] = useState(false);

  // Authoritative deadline (epoch ms) from the server, used to render the
  // countdown locally without trusting a client-side tick for anything.
  const deadlineRef = useRef(0);
  const roundRef = useRef(null);
  const myStakesRef = useRef({ 1: 0, 2: 0, 3: 0 });
  const configRef = useRef(null);
  // Our own uid, so we can pick our row out of the broadcast `winners[]`.
  const currentUserIdRef = useRef(null);
  // Resync watchdog bookkeeping (see the effect below).
  const lastServerEventRef = useRef(Date.now());
  const staleSinceRef = useRef(0);
  const lastResyncRef = useRef(0);
  const resyncStreakRef = useRef(0);

  useEffect(() => { roundRef.current = round; }, [round]);
  useEffect(() => { myStakesRef.current = myStakes; }, [myStakes]);
  useEffect(() => { configRef.current = config; }, [config]);

  const quickBets = (config && config.quickBetAmounts) || [];
  const frames = (config && config.frames) || [];
  const status = round ? round.status : "idle";
  const bettingOpen = status === "betting" && remainingMs > 0;

  const playCoinSound = useCallback(() => {
    if (!soundOn) return;
    try {
      const audio = new Audio(audioPath);
      audio.play().catch(() => {});
    } catch (_) {}
  }, [soundOn]);

  /**
   * Remember our own uid from whichever response carries it. `/balance` and
   * `/round/current` return it directly; `POST /bet` echoes it back inside the
   * created bet, which is the path that keeps this working against a backend
   * that hasn't picked up the route change yet.
   */
  const captureUserId = useCallback((res) => {
    const id = res && (res.userId || (res.bet && res.bet.userId));
    if (id) currentUserIdRef.current = String(id);
  }, []);

  const refreshBalance = useCallback(async () => {
    try {
      const res = await api.getBalance();
      captureUserId(res);
      if (res && typeof res.balance === "number") setBalance(res.balance);
    } catch (_) {}
  }, [captureUserId]);

  // ---- bootstrap: config + current round + socket ----
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const cfgRes = await api.getConfig();
        if (cancelled) return;
        const cfg = cfgRes && cfgRes.config;
        setConfig(cfg);
        if (!cfg || !cfg.enabled) {
          setBlocked(true);
          return;
        }
        if (cfg.quickBetAmounts && cfg.quickBetAmounts.length) {
          setSelectedCoin(cfg.quickBetAmounts[0]);
        }

        const current = await api.getCurrentRound();
        if (cancelled) return;
        applyRoundPayload(current);

        await connectSocket();
        if (cancelled) return;
        subscribeLobby();
      } catch (e) {
        if (!cancelled) setFatalError(e && e.message ? e.message : "load_failed");
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyRoundPayload = useCallback((payload) => {
    if (!payload) return;
    captureUserId(payload);
    if (payload.round) {
      // A snapshot or REST resync can land us in a round we never saw start
      // (missed `round_started` while the WebView was paused, or a reconnect).
      // Reset per-round state exactly as `round_started` does, or the previous
      // round's stakes and result panel carry over onto the new table.
      const prev = roundRef.current;
      if (prev && prev._id !== payload.round._id) {
        setMyStakes({ 1: 0, 2: 0, 3: 0 });
        if (payload.round.status === "betting") setResult(null);
      }
      roundRef.current = payload.round;
      setRound(payload.round);
      setBetsByFrame(payload.round.betsByFrame || {});
    }
    if (typeof payload.balance === "number") setBalance(payload.balance);
    if (Array.isArray(payload.leaderboard)) setLeaderboard(payload.leaderboard);
    if (typeof payload.remainingMs === "number") {
      setRemainingMs(payload.remainingMs);
      deadlineRef.current = Date.now() + payload.remainingMs;
    }
    if (Array.isArray(payload.myBets)) {
      const stakes = { 1: 0, 2: 0, 3: 0 };
      for (const b of payload.myBets) {
        stakes[b.frameId] = (stakes[b.frameId] || 0) + Number(b.amount || 0);
      }
      setMyStakes(stakes);
    }
  }, [captureUserId]);

  // ---- socket wiring ----
  useEffect(() => {
    const offs = [];

    for (const event of SERVER_EVENTS) {
      offs.push(on(event, () => { lastServerEventRef.current = Date.now(); }));
    }

    offs.push(on("teenpatti_state", (snap) => applyRoundPayload(snap)));
    offs.push(on("teenpatti_round_snapshot", (snap) => applyRoundPayload(snap)));

    offs.push(on("teenpatti_round_started", (p) => {
      // New round: my stakes and the previous result reset.
      setMyStakes({ 1: 0, 2: 0, 3: 0 });
      setResult(null);
      setBetsByFrame({});
      applyRoundPayload(p);
    }));

    offs.push(on("teenpatti_bet_placed", (p) => {
      if (!roundRef.current || p.roundId !== roundRef.current._id) return;
      setBetsByFrame(p.betsByFrame || {});
      if (Array.isArray(p.leaderboard)) setLeaderboard(p.leaderboard);
    }));

    offs.push(on("teenpatti_round_revealing", (p) => {
      setRound((prev) => (prev && prev._id === p.roundId
        ? { ...prev, status: "revealing", hands: p.hands, winningFrameId: p.winningFrameId, revealEndsAt: p.revealEndsAt }
        : prev));
      if (typeof p.remainingMs === "number") {
        setRemainingMs(p.remainingMs);
        deadlineRef.current = Date.now() + p.remainingMs;
      }

      // Show the result panel now rather than at `finished`: the winner is
      // already decided here and this phase runs ~10s, where `finished` runs 2.
      // The payout is an estimate until the settle transaction reports back,
      // and the panel labels it as one.
      const stakes = { ...(myStakesRef.current || {}) };
      setResult({
        pending: true,
        roundNumber: p.roundNumber,
        hands: p.hands,
        winningFrameId: p.winningFrameId,
        myStakes: stakes,
        my: buildMyResult({
          winners: null,
          currentUserId: currentUserIdRef.current,
          myStakes: stakes,
          estimatePayout: () => estimateTeenPattiWin(configRef.current, p.winningFrameId, stakes),
        }),
        top: null,
      });
    }));

    offs.push(on("teenpatti_round_finished", (p) => {
      const finished = p.round;
      // Snapshot our stakes before applyRoundPayload runs: that function
      // rewrites myStakes from payload.myBets, which this event happens not to
      // carry. Pin the ordering rather than depend on it staying that way.
      const stakes = { ...(myStakesRef.current || {}) };
      applyRoundPayload(p);
      if (!finished) return;

      // Display only. The diamonds were already credited server-side inside the
      // settle transaction; this patches the panel with what it actually paid.
      const currentUserId = currentUserIdRef.current;
      const winners = Array.isArray(p.winners) ? p.winners : [];
      const my = buildMyResult({
        winners,
        currentUserId,
        myStakes: stakes,
        estimatePayout: () => estimateTeenPattiWin(configRef.current, finished.winningFrameId, stakes),
      });
      const top = buildTopWinners(winners, currentUserId);

      // `prev` is normally the panel put up at `revealing`; it is null when a
      // reconnect drops us straight into `finished`, so build it from scratch.
      setResult((prev) => ({
        ...(prev || {}),
        pending: false,
        roundNumber: finished.roundNumber,
        hands: finished.hands || (prev && prev.hands) || null,
        winningFrameId: finished.winningFrameId,
        myStakes: stakes,
        my,
        top,
      }));

      refreshBalance();
      requestBalanceRefresh();
    }));

    offs.push(on("teenpatti_time_sync", (p) => {
      if (typeof p.remainingMs !== "number") return;
      setRemainingMs(p.remainingMs);
      deadlineRef.current = Date.now() + p.remainingMs;
      setRound((prev) => (prev && prev._id === p.roundId && prev.status !== p.status
        ? { ...prev, status: p.status }
        : prev));
    }));

    return () => { offs.forEach((fn) => fn && fn()); };
    // The handlers read config through configRef, so this no longer re-subscribes
    // when the config arrives — tearing every listener down and back up mid-round
    // could drop an event in the gap.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyRoundPayload, refreshBalance]);

  // Local countdown between server heartbeats.
  useEffect(() => {
    const id = setInterval(() => {
      setRemainingMs(Math.max(0, deadlineRef.current - Date.now()));
    }, 200);
    return () => clearInterval(id);
  }, []);

  // Resync watchdog. The table used to depend entirely on socket pushes: miss
  // one `round_started` (WebView paused in the background, a dropped socket, a
  // server engine restart) and it sat on "Next round starting" for ever. When
  // the table has not been in a live phase for longer than the between-round
  // gap, re-read the round over REST and make sure we are in the lobby room.
  useEffect(() => {
    let inFlight = false;
    const resync = async () => {
      const cfg = configRef.current;
      if (inFlight || !cfg || !cfg.enabled) return;
      inFlight = true;
      lastResyncRef.current = Date.now();
      try {
        ensureSubscribed();
        applyRoundPayload(await api.getCurrentRound());
      } catch (_) {
        // Next tick retries.
      } finally {
        inFlight = false;
      }
    };

    const id = setInterval(() => {
      const now = Date.now();
      setServerQuiet(now - lastServerEventRef.current > SERVER_QUIET_MS);

      const r = roundRef.current;
      const live = r
        && (r.status === "betting" || r.status === "revealing")
        && deadlineRef.current + 3000 > now;
      if (live) {
        staleSinceRef.current = 0;
        resyncStreakRef.current = 0;
        return;
      }
      if (!staleSinceRef.current) {
        staleSinceRef.current = now;
        return;
      }
      const cfg = configRef.current;
      const gapMs = ((cfg && Number(cfg.resultDurationSec)) || 5) * 1000 + 5000;
      const resyncGapMs = Math.min(
        MAX_RESYNC_GAP_MS,
        MIN_RESYNC_GAP_MS * 2 ** resyncStreakRef.current
      );
      if (now - staleSinceRef.current >= gapMs && now - lastResyncRef.current >= resyncGapMs) {
        resyncStreakRef.current += 1;
        resync();
      }
    }, WATCHDOG_TICK_MS);

    // Coming back from the background is exactly when pushes were missed.
    const onVisible = () => {
      if (document.visibilityState === "visible") resync();
    };
    document.addEventListener("visibilitychange", onVisible);
    const offReconnect = onSocketLifecycle("reconnect", resync);

    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
      offReconnect();
    };
  }, [applyRoundPayload]);

  // The result panel must never sit over a live betting table. `round_started`
  // clears it on the happy path, but a reconnect can arrive as a snapshot that
  // flips status straight to `betting` without that event.
  useEffect(() => {
    if (status === "betting") setResult(null);
  }, [status]);

  // Flutter teardown hook — matches GenericGameScreen's `__TEENPATTI_CLEANUP`.
  useEffect(() => {
    window.__TEENPATTI_CLEANUP = () => {
      try { disconnectSocket(); } catch (_) {}
    };
    return () => {
      disconnectSocket();
      delete window.__TEENPATTI_CLEANUP;
    };
  }, []);

  // ---- betting ----
  const placeBet = useCallback(async (frameId, amount) => {
    const currentRound = roundRef.current;
    if (!currentRound || currentRound.status !== "betting") {
      Toast("Betting is closed for this round");
      return;
    }
    if (!amount) {
      Toast("Select a coin first");
      return;
    }
    if (balance < amount) {
      Toast("You don't have enough diamonds, please recharge first!");
      return;
    }

    // Optimistic local feedback; the authoritative numbers arrive from the
    // server's response and the following bet_placed broadcast.
    haptic("light");
    playCoinSound();

    try {
      const res = await api.bet({
        roundId: currentRound._id,
        frameId,
        amount,
        clientNonce: makeNonce(),
      });
      if (res && res.ok) {
        captureUserId(res);
        if (typeof res.balance === "number") setBalance(res.balance);
        setMyStakes((prev) => ({ ...prev, [frameId]: (prev[frameId] || 0) + amount }));
        setLastBet({ frameId, amount });
      }
    } catch (e) {
      const code = (e && e.body && e.body.error) || e.message;
      const messages = {
        betting_closed: "Betting is closed for this round",
        insufficient_diamonds: "You don't have enough diamonds, please recharge first!",
        too_many_spots: "You've backed the maximum number of frames",
        frame_max_exceeded: "Frame bet limit reached",
        amount_below_min: "That stake is below the minimum",
        feature_disabled: "Teen Patti is currently unavailable",
      };
      Toast(messages[code] || "Bet failed, please try again");
      refreshBalance();
    }
  }, [balance, playCoinSound, refreshBalance, captureUserId]);

  const repeatLastBet = useCallback(() => {
    if (!lastBet) return;
    placeBet(lastBet.frameId, lastBet.amount);
  }, [lastBet, placeBet]);

  // ---- derived render state ----
  const showCards = status === "revealing" || status === "finished";
  const hands = (round && round.hands) || null;
  const winningFrameId = (round && round.winningFrameId) || null;

  const arrCards = hands
    ? hands.map((h) => (h.combination || []).slice(0, 3))
    : EMPTY_CARDS;

  const ribbons = hands
    ? hands.map((h, i) => {
        const frameId = frames[i] ? frames[i].id : i + 1;
        return [true, frameId === winningFrameId ? 1 : 0, h.combination ? h.combination[3] : ""];
      })
    : EMPTY_RIBBONS;

  const frameImages = frames.map((f, i) => {
    if (!showCards) return DARK_FRAMES[i];
    return f.id === winningFrameId ? WINNER_FRAMES[i] : LIGHT_FRAMES[i];
  });

  // Outcome-card inputs. The winning frame's index into `hands` is its position
  // in `frames`, the same mapping the ribbons above use.
  const winningFrameIdx = result
    ? frames.findIndex((f) => f.id === result.winningFrameId)
    : -1;
  const winningHand = result && result.hands && winningFrameIdx >= 0
    ? result.hands[winningFrameIdx]
    : null;
  const winningFrameName = result
    ? ((frames[winningFrameIdx] && frames[winningFrameIdx].name) || `Box ${result.winningFrameId}`)
    : "";

  const seconds = Math.ceil(remainingMs / 1000);
  let timerText = "";
  if (status === "betting") timerText = `${Math.max(0, seconds)}s`;
  else if (status === "revealing") timerText = "Showing Cards";
  // Only promise a round when the server is actually talking to us.
  else if (serverQuiet) timerText = "Waiting for next round…";
  else timerText = "Next round starting";

  if (fatalError) {
    return (
      <div className="grid board text-center">
        <p style={{ color: "#FDE188", marginTop: "40vh" }}>
          Couldn't load Teen Patti. Please try again.
        </p>
      </div>
    );
  }

  if (blocked) {
    return <GameBlock className="disabled" dialog setDialog={setBlocked} />;
  }

  return (
    <div>
      <div className="grid board text-center">
        <div className="row w-80 cardMargin">
          {frames.map((frame, i) => (
            <Frame
              key={frame.id}
              coins={Number(betsByFrame[String(frame.id)] || 0)}
              mine={Number(myStakes[frame.id] || 0)}
              gameCoin={quickBets}
              src={frameImages[i]}
              card1={arrCards[i] ? arrCards[i][0] : 0}
              card2={arrCards[i] ? arrCards[i][1] : 0}
              card3={arrCards[i] ? arrCards[i][2] : 0}
              arrCoins={potCoins(
                round ? round._id : "idle",
                frame.id,
                Number(betsByFrame[String(frame.id)] || 0),
                quickBets
              )}
              isVisibleRibbon={ribbons[i] ? ribbons[i][1] : 0}
              ribbonIdx={ribbons[i] ? ribbons[i][1] : 0}
              ribbonText={ribbons[i] ? ribbons[i][2] : ""}
              onClick={() => placeBet(frame.id, selectedCoin)}
              canCardShow={showCards}
              disabledClass={bettingOpen ? "" : "disabled"}
            />
          ))}
        </div>

        {status === "revealing" && <div className="showingCard">Showing Card</div>}

        <div className="row" style={{ padding: "0vh", height: "1vh" }}>
          <div>
            <img src={starCoin} className="UserCoin-images" alt="" />
          </div>
          <div
            className="col"
            style={{ color: "white", fontSize: "18px", marginTop: "-1vh", marginLeft: "3vh" }}
          >
            <p>
              <span style={{ fontWeight: "bolder" }}>{balance.toLocaleString()}</span>
            </p>
          </div>
        </div>

        <div className="row" style={{ marginTop: "-3vh" }}>
          {quickBets.map((coin, index) => (
            <CoinBtn
              key={coin}
              src={COIN_IMAGES[index % COIN_IMAGES.length]}
              number={coin}
              funcClick={() => setSelectedCoin(coin)}
              selectedClass={selectedCoin === coin ? "selectedCoin" : ""}
              disabledClass={bettingOpen ? "" : "disabled"}
            />
          ))}

          <div className="row">
            <button
              className={"btn " + (bettingOpen && lastBet ? "" : "disabled")}
              style={{ marginLeft: "5vh" }}
              onClick={repeatLastBet}
            >
              Repeat
            </button>
          </div>
        </div>

        <div className="row timer" style={{ opacity: 1 }}>
          <div className="col">{timerText}</div>
          <div className="col">
            <img className="clock" src={clock} alt="" />
          </div>
        </div>

        {leaderboard.length > 0 && (
          <div className="row topUsers">
            {leaderboard.map((entry) => (
              <Profile key={entry.rank} name={entry.userName} src={entry.userAvatar} />
            ))}
          </div>
        )}

        <div style={{ position: "absolute", top: "0px", left: "0px", margin: "5px" }}>
          <button
            className="musicBtn"
            style={{ backgroundImage: `url("${soundOn ? musicOn : musicOff}")` }}
            onClick={() => setSoundOn((s) => !s)}
          />
        </div>

        <div style={{ position: "absolute", top: "0px", right: "0px", margin: "5px" }}>
          <button
            className="historyIcon"
            onClick={() => setHistoryOpen(true)}
            style={{ backgroundImage: `url("${historyIcon}")`, marginRight: "8px" }}
          />
          {historyOpen && <History dialog={historyOpen} setDialog={setHistoryOpen} />}
          <button
            className="historyIcon"
            onClick={() => setRulesOpen(true)}
            style={{ backgroundImage: `url("${rulesIcon}")` }}
          />
          {rulesOpen && (
            <Rulse dialog={rulesOpen} setDialog={setRulesOpen} rulesText={config && config.rulesText} />
          )}
        </div>

        <RoundResultPanel
          open={!!result}
          pending={!!(result && result.pending)}
          // While the cards are still being revealed the panel shares the screen
          // with the table, so it must not dim or block it. The dim fades in only
          // once the round is settled.
          backdrop={!!(result && !result.pending)}
          roundNumber={result && result.roundNumber}
          myResult={result && result.my}
          topWinners={result && result.top}
          onClose={() => setResult(null)}
        >
          {result && (
            <div className="rr-outcome">
              <div className="rr-eyebrow">WINNING BOX</div>
              <div className="rr-frame-name">{winningFrameName}</div>
              {winningHand && (
                <>
                  <div className="rr-hand-category">{winningHand.combination[3]}</div>
                  <div className="rr-cards">
                    {winningHand.combination.slice(0, 3).map((n, i) => (
                      <img key={i} className="rr-card" src={CARD_IMAGES[n]} alt="" />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </RoundResultPanel>

        <ToastContainer />
      </div>
    </div>
  );
}

export default App;
